import { Component, computed, input, output, signal, viewChild } from '@angular/core';
import { delay, shuffle } from '@gametailors/web-core/misc';
import {
  RidMechanicBackground,
  RidMechanicContent,
  RidMechanicTitle,
  RidMechanicWrapper,
  RidOverlayFeedback,
  RidTopBar,
} from '../../components';
import { RidMemoryCard } from '../../components/rid-memory-card/rid-memory-card';
import { AudioEffectPlayer } from '../../utils/audio.utils';

type TContent =
  | { type: 'TEXT'; text: string }
  | { type: 'IMAGE'; image: string }
  | { type: 'AUDIO'; audio: string | { pronounce: string } };

interface MemoryCard {
  cardId: string;
  pairId: string;
  content: TContent;
}

@Component({
  selector: 'rid-memory-match',
  imports: [RidMechanicBackground, RidMechanicWrapper, RidTopBar, RidMechanicContent, RidMechanicTitle, RidMemoryCard, RidOverlayFeedback],
  templateUrl: './rid-memory-match.html',
  host: {
    class: 'block h-dvh',
  },
})
export class RidMemoryMatch {
  pairs = input.required<
    {
      card1: { content: TContent };
      card2: { content: TContent };
    }[]
  >();

  cards = computed<MemoryCard[]>(() =>
    shuffle(
      this.pairs().flatMap((pair) => {
        const id = crypto.randomUUID();
        return [
          { cardId: `${id}-a`, pairId: id, content: pair.card1.content },
          { cardId: `${id}-b`, pairId: id, content: pair.card2.content },
        ];
      }),
    ),
  );

  columnCount = computed(() => Math.ceil(Math.sqrt(this.cards().length)));

  flippedCards = signal<string[]>([]);
  completedPairs = signal<string[]>([]);
  isChecking = signal(false);
  areFlippedIncorrect = signal(false);
  feedbackAnimsReceived = 0;

  overlayFeedback = viewChild.required<RidOverlayFeedback>('overlayFeedback');

  finished = output<void>();

  cardState(card: MemoryCard): 'HIDDEN' | 'FLIPPED' | 'CORRECT' | 'INCORRECT' {
    if (this.completedPairs().includes(card.pairId)) return 'CORRECT';
    if (!this.flippedCards().includes(card.cardId)) return 'HIDDEN';
    return this.areFlippedIncorrect() ? 'INCORRECT' : 'FLIPPED';
  }

  async flip(card: MemoryCard) {
    if (this.isChecking() || this.cardState(card) !== 'HIDDEN') return;

    const flipped = [...this.flippedCards(), card.cardId];
    this.flippedCards.set(flipped);

    AudioEffectPlayer.play('BUBBLE_POP');

    if (this.flippedCards().length < 2) return;
    this.isChecking.set(true);

    await delay(500);

    const firstCard = this.cards().find((c) => c.cardId === flipped[0])!;
    const secondCard = this.cards().find((c) => c.cardId === flipped[1])!;
    const correct = firstCard.pairId === secondCard.pairId;

    if (correct) {
      const completed = [...this.completedPairs(), firstCard.pairId];
      this.completedPairs.set(completed);

      AudioEffectPlayer.play('CORRECT');
      this.overlayFeedback().showCorrect();
    } else {
      this.areFlippedIncorrect.set(true);

      AudioEffectPlayer.play('INCORRECT');
      this.overlayFeedback().showIncorrect();
    }
  }

  onFeedbackAnimEnd() {
    this.feedbackAnimsReceived++;
    if (this.feedbackAnimsReceived < 2) return;

    this.feedbackAnimsReceived = 0;
    this.finishCheck();
  }

  finishCheck() {
    if (!this.isChecking()) return;

    this.flippedCards.set([]);
    this.isChecking.set(false);
    this.areFlippedIncorrect.set(false);

    if (this.completedPairs().length === this.pairs().length) {
      this.finished.emit();
    }
  }
}
